import { useCallback, useEffect, useRef } from 'react';

const FIREBASE_SW_SCOPE = '/firebase-cloud-messaging-push-scope';
const NOTIFICATION_TAG = 'admin-mfa-expiry';

/**
 * Tells a member whose attention is elsewhere that their admin session is about
 * to end.
 *
 * Generated in the browser — no FCM message, no push subscription, nothing
 * enqueued in the Message service. It reuses the primitive already in
 * useFirebaseNotification: take a registered service worker and call
 * showNotification on it. Calling that outside an onMessage handler is what
 * makes it local.
 *
 * Never prompts for permission. The app only ever acquires it as a side effect
 * of messaging.getToken(), so members who accepted push already have it and
 * everyone else is 'default' or 'denied'. A permission dialog appearing
 * unprompted three minutes before an expiry is hostile, and browsers ignore
 * requests made without a user gesture in any case. With no permission this
 * falls back to the tab title, which needs no permission at all.
 *
 * Fired by useAdminSessionWatcher at the moment it shows a warning, rather than
 * deciding for itself when a warning is due. One decision, two outputs: the
 * modal and the notification cannot disagree about which rung was reached, and
 * each rung of the ladder notifies rather than only the first.
 *
 * @param {Boolean} isWarningDue whether the session is inside its warning band
 * @returns {Object} onNotify to raise one, and onRestoreTitle
 */
const useAdminSessionNotification = (isWarningDue: boolean) => {
    const originalTitle = useRef<string | null>(null);

    const onRestoreTitle = useCallback(() => {
        if (originalTitle.current !== null) {
            document.title = originalTitle.current;
            originalTitle.current = null;
        }
    }, []);

    /**
     * Deliver through whichever channel this browser actually has.
     *
     * Three attempts, because the first two are each commonly unavailable in
     * ways that are not errors. The firebase worker is only registered once
     * something has asked for a push token, which may never have happened;
     * `serviceWorker.ready` covers a differently-scoped worker; and a plain
     * Notification needs no worker at all, which is the usual desktop case.
     * Previously only the first was tried, so a browser with permission but no
     * firebase worker fell all the way through to the tab title.
     *
     * @param {String} body the message
     * @returns {Promise<Boolean>} whether anything was shown
     */
    const onDeliver = useCallback(async (body: string) => {
        if (typeof Notification === 'undefined') return false;
        if (Notification.permission !== 'granted') return false;

        const options: NotificationOptions = {
            body,
            // Replaces rather than stacks, so three rungs leave one notification.
            tag: NOTIFICATION_TAG,
            // Don't self-dismiss while the member is away — the whole point is
            // that they are not looking at this tab.
            requireInteraction: true,
            data: { url: window.location.pathname, tag: NOTIFICATION_TAG },
        };

        if ('serviceWorker' in navigator) {
            try {
                const registration =
                    (await navigator.serviceWorker.getRegistration(FIREBASE_SW_SCOPE)) ||
                    (await navigator.serviceWorker.ready);

                if (registration) {
                    await registration.showNotification('Admin session ending', options);

                    return true;
                }
            } catch (error) {
                console.log('Admin session notification: service worker unavailable', error);
                // Fall through to the worker-less path below.
            }
        }

        try {
            // eslint-disable-next-line no-new
            new Notification('Admin session ending', options);

            return true;
        } catch (error) {
            // No permission, or a browser that only allows worker notifications.
            // The caller falls back to the tab title, which needs neither.
            console.log('Admin session notification: could not be raised', error);

            return false;
        }
    }, []);

    /**
     * Raise one notification for the rung just reached.
     *
     * Focus, not visibility. visibilityState only goes 'hidden' when the tab is
     * backgrounded or the window minimised — switch to another application, or
     * another window that does not cover this one, and the page stays 'visible'
     * while plainly not being looked at. Gating on visibility alone meant the
     * notification never fired in the case it exists for.
     *
     * @param {Number} remainingSeconds life left, for the message
     * @returns {void}
     */
    const onNotify = useCallback(
        (remainingSeconds: number) => {
            const isAway = !document.hasFocus() || document.visibilityState === 'hidden';

            if (!isAway) return;

            const minutes = Math.ceil(remainingSeconds / 60);
            const left =
                remainingSeconds < 60
                    ? `${Math.max(remainingSeconds, 1)} seconds`
                    : `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;

            onDeliver(`You have ${left} left in Admin. Come back to the tab to stay in.`).then(
                shown => {
                    if (shown) return;

                    // No permission, or nothing to show it with. The tab title
                    // reaches a member scanning their tab strip regardless.
                    if (originalTitle.current === null) {
                        originalTitle.current = document.title;
                    }

                    const label = remainingSeconds < 60 ? `${remainingSeconds}s` : `${minutes}m`;

                    document.title = `(${label}) Admin ending - ${originalTitle.current}`;
                }
            );
        },
        [onDeliver]
    );

    // Nothing to warn about any more, or the member is back: put the title back.
    useEffect(() => {
        if (!isWarningDue) onRestoreTitle();
    }, [isWarningDue, onRestoreTitle]);

    useEffect(() => {
        const onReturn = () => {
            if (document.hasFocus()) onRestoreTitle();
        };

        window.addEventListener('focus', onReturn);
        document.addEventListener('visibilitychange', onReturn);

        return () => {
            window.removeEventListener('focus', onReturn);
            document.removeEventListener('visibilitychange', onReturn);
        };
    }, [onRestoreTitle]);

    useEffect(() => onRestoreTitle, [onRestoreTitle]);

    return { onNotify, onRestoreTitle };
};

export default useAdminSessionNotification;
